import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { useAnalytics } from "../hooks/useAnalytics";
import "./Alphabets.css";

const Alphabets = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { alphabets, updateAlphabet, loading } = useData();
  const { trackAudio, trackAdmin } = useAnalytics();
  const [editingAlphabet, setEditingAlphabet] = useState(null);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleEditAlphabet = (alphabet) => {
    setEditingAlphabet({ ...alphabet });
  };

  const handleSaveAlphabet = async () => {
    try {
      await updateAlphabet(editingAlphabet.id, editingAlphabet);
      trackAdmin("edit_alphabet", "alphabet", editingAlphabet.id);
      setEditingAlphabet(null);
    } catch (error) {
      alert("Failed to save changes. Please try again.");
    }
  };

  const handleEditCharacter = (alphabetId, characterIndex, character) => {
    setEditingCharacter({ alphabetId, characterIndex, ...character });
    // Reset recording state when starting to edit
    setRecordedAudio(null);
    setIsRecording(false);
    setMediaRecorder(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedAudio(url);
        // Stop all tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Failed to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSaveCharacter = async () => {
    try {
      const alphabet = alphabets.find(
        (a) => a.id === editingCharacter.alphabetId
      );
      const updatedCharacters = [...alphabet.characters];
      updatedCharacters[editingCharacter.characterIndex] = {
        char: editingCharacter.char,
        romaji: editingCharacter.romaji,
        audio: editingCharacter.audio,
      };

      await updateAlphabet(alphabet.id, {
        ...alphabet,
        characters: updatedCharacters,
      });

      trackAdmin(
        "edit_character",
        "character",
        `${alphabet.id}:${editingCharacter.characterIndex}`
      );
      setEditingCharacter(null);
    } catch (error) {
      alert("Failed to save character. Please try again.");
    }
  };

  const playAudio = (audioUrl, characterInfo) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch((e) => console.log("Error playing audio:", e));
      trackAudio("character", characterInfo);
    }
  };

  if (loading) {
    return (
      <div className="alphabets">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="alphabets">
      <div className="header">
        <button className="back-button" onClick={handleBackToHome}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
      </div>

      <div className="main-content">
        <h1 className="title">Japanese Alphabets</h1>

        <div className="alphabet-sections">
          {alphabets.map((alphabet) => (
            <div key={alphabet.id} className="alphabet-card">
              <div className="alphabet-header">
                <h2>
                  {editingAlphabet && editingAlphabet.id === alphabet.id ? (
                    <input
                      type="text"
                      value={editingAlphabet.name}
                      onChange={(e) =>
                        setEditingAlphabet({
                          ...editingAlphabet,
                          name: e.target.value,
                        })
                      }
                      className="edit-input"
                    />
                  ) : (
                    alphabet.name
                  )}
                </h2>
                {isAdmin && (
                  <div className="admin-controls">
                    {editingAlphabet && editingAlphabet.id === alphabet.id ? (
                      <>
                        <button
                          onClick={handleSaveAlphabet}
                          className="save-btn"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingAlphabet(null)}
                          className="cancel-btn"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEditAlphabet(alphabet)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              <p>
                {editingAlphabet && editingAlphabet.id === alphabet.id ? (
                  <textarea
                    value={editingAlphabet.description}
                    onChange={(e) =>
                      setEditingAlphabet({
                        ...editingAlphabet,
                        description: e.target.value,
                      })
                    }
                    className="edit-textarea"
                  />
                ) : (
                  alphabet.description
                )}
              </p>

              <div className="example-grid">
                {alphabet.characters?.map((character, index) => (
                  <div key={index} className="character-item">
                    {editingCharacter &&
                    editingCharacter.alphabetId === alphabet.id &&
                    editingCharacter.characterIndex === index ? (
                      <div className="character-edit">
                        <input
                          type="text"
                          value={editingCharacter.char}
                          onChange={(e) =>
                            setEditingCharacter({
                              ...editingCharacter,
                              char: e.target.value,
                            })
                          }
                          className="char-input"
                          placeholder="Character"
                        />
                        <input
                          type="text"
                          value={editingCharacter.romaji}
                          onChange={(e) =>
                            setEditingCharacter({
                              ...editingCharacter,
                              romaji: e.target.value,
                            })
                          }
                          className="romaji-input"
                          placeholder="Romaji"
                        />
                        <input
                          type="url"
                          value={editingCharacter.audio}
                          onChange={(e) =>
                            setEditingCharacter({
                              ...editingCharacter,
                              audio: e.target.value,
                            })
                          }
                          className="audio-input"
                          placeholder="MP3 URL"
                        />
                        <div className="audio-recording-controls">
                          {!isRecording ? (
                            <button
                              onClick={startRecording}
                              className="record-btn"
                              title="Start recording"
                            >
                              🎤 Record
                            </button>
                          ) : (
                            <button
                              onClick={stopRecording}
                              className="stop-btn"
                              title="Stop recording"
                            >
                              ⏹️ Stop
                            </button>
                          )}
                          {recordedAudio && (
                            <>
                              <audio
                                src={recordedAudio}
                                controls
                                className="audio-preview"
                              />
                              <button
                                onClick={() => {
                                  setEditingCharacter({
                                    ...editingCharacter,
                                    audio: recordedAudio,
                                  });
                                }}
                                className="use-recording-btn"
                                title="Use this recording"
                              >
                                ✓ Use Recording
                              </button>
                            </>
                          )}
                        </div>
                        <div className="character-controls">
                          <button
                            onClick={handleSaveCharacter}
                            className="save-btn"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCharacter(null)}
                            className="cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="character-display">
                        <div className="character">{character.char}</div>
                        <div className="romaji">{character.romaji}</div>
                        <div className="character-controls">
                          {character.audio && (
                            <button
                              onClick={() =>
                                playAudio(
                                  character.audio,
                                  `${alphabet.id}:${character.char}`
                                )
                              }
                              className="audio-btn"
                              title="Play pronunciation"
                            >
                              🔊
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() =>
                                handleEditCharacter(
                                  alphabet.id,
                                  index,
                                  character
                                )
                              }
                              className="edit-char-btn"
                              title="Edit character"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Character Modal */}
      {editingCharacter && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Character</h3>
            <div className="edit-form">
              <label>
                Character:
                <input
                  type="text"
                  value={editingCharacter.char}
                  onChange={(e) =>
                    setEditingCharacter({
                      ...editingCharacter,
                      char: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Romaji:
                <input
                  type="text"
                  value={editingCharacter.romaji}
                  onChange={(e) =>
                    setEditingCharacter({
                      ...editingCharacter,
                      romaji: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Audio URL (MP3):
                <input
                  type="url"
                  value={editingCharacter.audio}
                  onChange={(e) =>
                    setEditingCharacter({
                      ...editingCharacter,
                      audio: e.target.value,
                    })
                  }
                  placeholder="https://example.com/audio.mp3"
                />
              </label>
              <div className="audio-recording-section">
                <label>Record Audio:</label>
                <div className="audio-recording-controls">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      className="record-btn"
                      title="Start recording"
                    >
                      🎤 Record
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="stop-btn"
                      title="Stop recording"
                    >
                      ⏹️ Stop
                    </button>
                  )}
                  {recordedAudio && (
                    <>
                      <audio
                        src={recordedAudio}
                        controls
                        className="audio-preview"
                      />
                      <button
                        onClick={() => {
                          setEditingCharacter({
                            ...editingCharacter,
                            audio: recordedAudio,
                          });
                        }}
                        className="use-recording-btn"
                        title="Use this recording"
                      >
                        ✓ Use Recording
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-controls">
                <button onClick={handleSaveCharacter} className="save-btn">
                  Save
                </button>
                <button
                  onClick={() => setEditingCharacter(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alphabets;
